import React, { useEffect, useMemo, useState } from "react";
import {
  subscribeToVotingProjects,
  submitVote,
  removeVote,
  hasDeviceVotedForProject,
  VotingProject,
} from "../../../services/votingService";
import {
  getDeviceFingerprint,
  markDeviceAsVoted,
  hasDeviceVoted,
} from "../../../utils/deviceFingerprint";
import { useNavigate } from "react-router-dom";

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [sortKey, setSortKey] = useState("1"); // 1: Latest, 2: Most Voted, 3: A->Z
  const [projects, setProjects] = useState<VotingProject[]>([]);
  const [loading, setLoading] = useState(true);
  const [votingStates, setVotingStates] = useState<Record<string, boolean>>({});
  const [votingInProgress, setVotingInProgress] = useState<
    Record<string, boolean>
  >({});
  const [deviceFingerprint, setDeviceFingerprint] = useState<string | null>(
    null
  );

  // Initialize device fingerprint
  useEffect(() => {
    const initFingerprint = async () => {
      const fingerprint = await getDeviceFingerprint();
      setDeviceFingerprint(fingerprint);

      // Check voting states for all projects
      const states: Record<string, boolean> = {};
      for (const project of projects) {
        const hasVoted = await hasDeviceVotedForProject(
          project.id,
          fingerprint
        );
        states[project.id] = hasVoted;
      }
      setVotingStates(states);
    };

    if (projects.length > 0) {
      initFingerprint();
    }
  }, [projects]);

  // Subscribe to real-time project updates
  useEffect(() => {
    const unsubscribe = subscribeToVotingProjects((updatedProjects) => {
      setProjects(updatedProjects);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Update voting states when projects change
  useEffect(() => {
    const updateVotingStates = async () => {
      if (!deviceFingerprint || projects.length === 0) return;

      const states: Record<string, boolean> = {};
      for (const project of projects) {
        const hasVoted = await hasDeviceVotedForProject(
          project.id,
          deviceFingerprint
        );
        states[project.id] = hasVoted;
      }
      setVotingStates(states);
    };

    updateVotingStates();
  }, [projects, deviceFingerprint]);

  const filtered = useMemo(() => {
    let list = projects.filter((p) =>
      [p.name || "", p.group || ""].some((v) =>
        v.toLowerCase().includes(query.toLowerCase())
      )
    );

    if (sortKey === "1") {
      // Latest - already sorted by creation date
      list = [...list].sort((a, b) => {
        const aTime = a.createdAt?.toMillis() || 0;
        const bTime = b.createdAt?.toMillis() || 0;
        return bTime - aTime;
      });
    } else if (sortKey === "2") {
      // Most Voted - already sorted by vote count
      list = [...list].sort((a, b) => (b.voteCount || 0) - (a.voteCount || 0));
    } else if (sortKey === "3") {
      // A → Z
      list = [...list].sort((a, b) =>
        (a.name || "").localeCompare(b.name || "")
      );
    }

    return list;
  }, [projects, query, sortKey]);

  const vote = async (projectId: string) => {
    if (!deviceFingerprint) {
      alert("Device fingerprint not initialized. Please refresh the page.");
      return;
    }

    if (votingInProgress[projectId]) {
      return;
    }

    const hasVoted = votingStates[projectId];

    setVotingInProgress((prev) => ({ ...prev, [projectId]: true }));

    try {
      if (hasVoted) {
        // Remove vote
        const result = await removeVote(projectId, deviceFingerprint);
        if (result.success) {
          setVotingStates((prev) => ({ ...prev, [projectId]: false }));
          await hasDeviceVoted(projectId).then((localVoted) => {
            if (localVoted) {
              // Clear local storage vote
              const votedProjects = JSON.parse(
                localStorage.getItem("votedProjects") || "[]"
              ) as string[];
              const filtered = votedProjects.filter((id) => id !== projectId);
              localStorage.setItem("votedProjects", JSON.stringify(filtered));
            }
          });
        } else {
          alert(result.message);
        }
      } else {
        // Submit vote
        const result = await submitVote(projectId, deviceFingerprint);
        if (result.success) {
          setVotingStates((prev) => ({ ...prev, [projectId]: true }));
          await markDeviceAsVoted(projectId);
        } else {
          alert(result.message);
        }
      }
    } catch (error) {
      console.error("Error voting:", error);
      alert("An error occurred while processing your vote.");
    } finally {
      setVotingInProgress((prev) => ({ ...prev, [projectId]: false }));
    }
  };

  return (
    <div className="w-full max-w-[1400px] mx-auto px-4 sm:px-6 py-6 sm:py-8 min-h-[calc(100vh-64px)]">
      <div className="flex flex-col sm:flex-row sm:justify-end sm:items-center gap-4 sm:gap-6 sticky z-[100] bg-[rgba(255,255,255,0.95)] dark:bg-transparent backdrop-blur-[20px] rounded-2xl transition-all duration-300 p-4 sm:p-0">
        <div className="flex-1 w-full sm:max-w-[500px]">
          <form
            className="flex items-center relative w-full bg-[rgba(255,255,255,0.8)] dark:bg-[rgba(30,41,59,0.8)] border-2 border-[rgba(59,130,246,0.2)] dark:border-[rgba(59,130,246,0.3)] rounded-xl transition-all duration-300 overflow-hidden focus-within:border-[var(--primary-color)] focus-within:shadow-[0_0_0_4px_rgba(37,99,235,0.1)]"
            onSubmit={(e) => {
              e.preventDefault();
            }}
          >
            <input
              type="text"
              placeholder="Search projects..."
              className="flex-1 py-3 sm:py-3.5 px-4 sm:px-5 text-sm sm:text-base border-none outline-none bg-transparent text-[var(--text-primary)] placeholder:text-[var(--text-secondary)]"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            <button
              type="submit"
              className="py-3 sm:py-3.5 px-4 sm:px-6 bg-gradient-to-r from-[var(--gradient-primary)] to-[var(--gradient-primary)] border-none text-white cursor-pointer transition-all duration-300 flex items-center justify-center text-sm sm:text-base hover:bg-gradient-to-r hover:from-[var(--gradient-secondary)] hover:to-[var(--gradient-secondary)] hover:scale-105 hover:shadow-[0_4px_12px_rgba(37,99,235,0.3)] active:scale-[0.98] mr-2"
            >
              <i className="fa-solid fa-magnifying-glass"></i>
            </button>
          </form>
        </div>
        <select
          name="projects"
          className="w-full sm:w-auto py-3 sm:py-3.5 px-4 sm:px-6 text-sm sm:text-base bg-[rgba(255,255,255,0.9)] dark:bg-[rgba(30,41,59,0.9)] border-2 border-[rgba(59,130,246,0.2)] dark:border-[rgba(59,130,246,0.3)] rounded-xl text-[var(--text-primary)] cursor-pointer transition-all duration-300 sm:min-w-[180px] appearance-none bg-[url('data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%232563eb' d='M6 9L1 4h10z'/%3E%3C/svg%3E')] bg-[length:12px_12px] bg-no-repeat bg-[right_1rem_center] pr-10 hover:border-[var(--primary-color)] hover:shadow-[0_0_0_4px_rgba(37,99,235,0.1)] focus:outline-none focus:border-[var(--primary-color)] focus:shadow-[0_0_0_4px_rgba(37,99,235,0.1)]"
          value={sortKey}
          onChange={(e) => setSortKey(e.target.value)}
        >
          <option value="1">Latest</option>
          <option value="2">Most Voted</option>
          <option value="3">A → Z</option>
        </select>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="text-[var(--text-primary)] text-xl">
            Loading projects...
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-[repeat(auto-fill,minmax(280px,1fr))] md:grid-cols-[repeat(auto-fill,minmax(320px,1fr))] gap-6 sm:gap-8 mt-6 sm:mt-8">
          {filtered.length > 0 ? (
            filtered.map((p) => {
              const hasVoted = votingStates[p.id] || false;
              const isVoting = votingInProgress[p.id] || false;

              return (
                <div
                  className="flex flex-col bg-[rgba(255,255,255,0.95)] dark:bg-[rgba(30,41,59,0.95)] backdrop-blur-[20px] rounded-[20px] overflow-hidden border border-[rgba(59,130,246,0.2)] dark:border-[rgba(59,130,246,0.3)] shadow-[0_4px_20px_rgba(37,99,235,0.1)] dark:shadow-[0_4px_20px_rgba(0,0,0,0.3)] transition-all duration-300 cursor-pointer h-full hover:-translate-y-2 hover:shadow-[0_12px_40px_rgba(37,99,235,0.2)] dark:hover:shadow-[0_12px_40px_rgba(59,130,246,0.3)] hover:border-[var(--primary-color)] group"
                  key={p.id}
                  onClick={() => {
                    navigate(`/profile/${p.id}`);
                  }}
                >
                  <div className="w-full h-[240px] overflow-hidden bg-gradient-to-br from-[rgba(37,99,235,0.1)] to-[rgba(14,165,233,0.1)] flex items-center justify-center relative">
                    {p.poster ? (
                      <img
                        src={p.poster}
                        alt={p.name || "Project poster"}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-[var(--text-secondary)] text-[3rem] opacity-50">
                        <i className="fa-solid fa-image"></i>
                      </div>
                    )}
                  </div>
                  <div className="p-6 flex flex-col gap-3 flex-1">
                    <h3 className="text-[1.5rem] font-bold text-[var(--text-primary)] m-0 leading-tight transition-colors duration-300 group-hover:text-[var(--primary-color)]">
                      {p.name || "Untitled Project"}
                    </h3>
                    <p className="text-base text-[var(--text-secondary)] m-0 font-medium">
                      {p.group || "No group"}
                    </p>
                    <div className="mt-auto flex items-center justify-between gap-2">
                      <span className="text-sm text-[var(--text-secondary)] font-medium">
                        <i className="fa-solid fa-heart text-[#ef4444] mr-1"></i>
                        {p.voteCount || 0} votes
                      </span>
                      <button
                        disabled={isVoting}
                        className={`py-3.5 px-6 text-white border-none rounded-[10px] text-base font-semibold cursor-pointer transition-all duration-300 flex items-center justify-center gap-2 shadow-[0_4px_12px_rgba(37,99,235,0.2)] hover:-translate-y-0.5 hover:shadow-[0_6px_20px_rgba(37,99,235,0.3)] active:translate-y-0 disabled:opacity-50 disabled:cursor-not-allowed ${
                          hasVoted
                            ? "bg-gradient-to-r from-[#ef4444] to-[#dc2626] hover:from-[#dc2626] hover:to-[#b91c1c]"
                            : "bg-gradient-to-r from-[var(--gradient-primary)] to-[var(--gradient-primary)] hover:from-[var(--gradient-secondary)] hover:to-[var(--gradient-secondary)]"
                        }`}
                        onClick={(e) => {
                          e.stopPropagation();
                          vote(p.id);
                        }}
                      >
                        <i
                          className={`fa-solid ${
                            isVoting
                              ? "fa-spinner fa-spin"
                              : hasVoted
                              ? "fa-heart"
                              : "fa-heart"
                          } text-[0.9rem]`}
                        ></i>
                        {isVoting
                          ? "Processing..."
                          : hasVoted
                          ? "Voted"
                          : "Vote"}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="col-span-full flex flex-col items-center justify-center py-16 px-8 text-center text-[var(--text-secondary)]">
              <i className="fa-solid fa-inbox text-[4rem] mb-4 opacity-50"></i>
              <p className="text-[1.25rem] m-0">
                {query
                  ? "No projects found matching your search."
                  : "No projects available."}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Dashboard;
